export interface SessionPersistenceAdapter {
  read<T>(key: string): Promise<T | null>;
  write<T>(key: string, value: T | null): Promise<void>;
}

const GLOBAL_ADAPTER_KEY = '__PROP_VISION_SESSION_PERSISTENCE__';

function getGlobalAdapter(): SessionPersistenceAdapter | undefined {
  return (globalThis as Record<string, unknown>)[GLOBAL_ADAPTER_KEY] as SessionPersistenceAdapter | undefined;
}

function setGlobalAdapter(adapter: SessionPersistenceAdapter | undefined): void {
  if (adapter) {
    (globalThis as Record<string, unknown>)[GLOBAL_ADAPTER_KEY] = adapter;
  } else {
    delete (globalThis as Record<string, unknown>)[GLOBAL_ADAPTER_KEY];
  }
}

function createBrowserAdapter(): SessionPersistenceAdapter {
  const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

  return {
    async read<T>(key: string): Promise<T | null> {
      if (!isBrowser) {
        return null;
      }
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
      } catch (error) {
        console.warn('Failed to read session from localStorage', error);
        return null;
      }
    },
    async write<T>(key: string, value: T | null): Promise<void> {
      if (!isBrowser) {
        return;
      }
      try {
        if (value === null) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(value));
        }
      } catch (error) {
        console.warn('Failed to persist session to localStorage', error);
      }
    },
  };
}

const browserAdapter = createBrowserAdapter();

export function setSessionPersistenceAdapter(adapter: SessionPersistenceAdapter | undefined): void {
  setGlobalAdapter(adapter);
}

export async function readPersistedValue<T>(key: string): Promise<T | null> {
  const adapter = getGlobalAdapter() ?? browserAdapter;
  return adapter.read<T>(key);
}

export async function writePersistedValue<T>(key: string, value: T | null): Promise<void> {
  const adapter = getGlobalAdapter() ?? browserAdapter;
  await adapter.write<T>(key, value);
}